import { ChangeEvent, FormEvent, useState } from "react";
import GeneralInput from "../../common/GeneralInput";
import Modal from "../../common/Modal";
import { Zone } from "../../../types/common.types";
import { useAdmin } from "../../../context/admin";
import { AiOutlineCloseCircle } from "react-icons/ai";
import AdminService from "../../../services/admin.service";
import Checkbox from "../../common/Checkbox";
import FileInput from "../../common/FileInput";
import jsonToFormData from "../../../utils/jsonToFormData";
import { useGlobal } from "../../../context/global";

// Function to convert image to WebP
async function convertToWebP(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    // Check if file is already WebP
    if (file.type === 'image/webp') {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Draw image on canvas
        ctx.drawImage(img, 0, 0);
        
        // Convert to WebP with quality settings
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not convert image to WebP'));
              return;
            }
            
            // Create new File object with WebP extension
            const webpFile = new File(
              [blob],
              `${file.name.split('.')[0] || 'image'}.webp`,
              { type: 'image/webp' }
            );
            resolve(webpFile);
          },
          'image/webp',
          0.85 // 85% quality - adjust as needed
        );
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
}

export default function CreateZone() {
    const { setIsCreateZoneModalOpen } = useAdmin()
    const { setIsLoading, setMessage } = useGlobal()
    const [data, setData] = useState<Partial<Zone>>({
        id: '',
        navbar: false,
    } as Zone)

    async function submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        try {
            // Convert cover image to WebP before submitting
            let processedData = { ...data };
            
            if (processedData.cover instanceof File) {
              try {
                processedData.cover = await convertToWebP(processedData.cover);
                setMessage({
                  type: 'success',
                  content: 'Image converted to WebP'
                });
                setTimeout(() => {
                  setMessage(undefined);
                }, 2000);
              } catch (error) {
                console.error('Error converting cover to WebP:', error);
                setMessage({
                  type: 'error',
                  content: 'Uploading image in original format'
                });
                // Keep original file if conversion fails
              }
            }
            
            const formData = jsonToFormData(processedData)
            await AdminService.registerZone(formData)
            window.location.reload()
        } catch (error) {
            console.log(error);
            setMessage({
              type: 'error',
              content: 'Error creating zone'
            });
            setTimeout(() => {
              setMessage(undefined);
            }, 2500);
        } finally {
            setIsLoading(false);
        }
    }

    function onChange(e: ChangeEvent<HTMLInputElement>) {
        const value = e.target.value
        const name = e.target.name
        setData(prevData  => ({...prevData, [name]: value }))
    }

    // Function to handle file upload with WebP conversion
    const handleFileChange = async (newFile: File) => {
      try {
        setIsLoading(true);
        const webpFile = await convertToWebP(newFile);
        setData(prevData => ({...prevData, cover: webpFile}));
        setMessage({
          type: 'success',
          content: `Cover image converted to WebP (${Math.round(webpFile.size / 1024)}KB)`
        });
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } catch (error) {
        console.error('Error converting to WebP:', error);
        setMessage({
          type: 'error',
          content: 'Failed to convert image. Uploading original format.'
        });
        // Fallback: use original file
        setData(prevData => ({...prevData, cover: newFile}));
        setTimeout(() => {
          setMessage(undefined);
        }, 2000);
      } finally {
        setIsLoading(false);
      }
    };

    return  (
        <Modal
        onSubmit={submit}
        >
            <h1>Create zone</h1>
            <div className="input-container">
                <Checkbox
                checked={data.navbar}
                label="Show in navbar"
                onChange={(checked: boolean) => setData(prevData => ({...prevData, navbar: checked}))}
                />
            </div>
            <div className="input-container"
            
            >
                <GeneralInput
                name="id"
                label="ID"
                value={data.id}
                onChange={onChange}
                />
            </div>
            <div className="input-container">
                <FileInput
                value={data.cover}
                style={{height: '100px'}}
                label="Cover (will be converted to WebP)"
                accept=".png, .jpg, .jpeg, .heic, .webp"
                onChange={handleFileChange}
                deleteFile={() => setData(prevData => {
                    const { cover, ...rest } = prevData;
                    return rest;
                })}
                />
            </div>
            <div className="input-container">
            <div className="input-container">
                <GeneralInput
                label="Overview (en)"
                value={data.overview || ''}
                name="overview"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (es)"
                value={data.overview_es || ''}
                name="overview_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Overview (pt)"
                value={data.overview_pt || ''}
                name="overview_pt"
                onChange={onChange}
                />
            </div>
            </div>
            <div className="input-container">
                <GeneralInput
                label="Description (en)"
                value={data.description || ''}
                name="description"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (es)"
                value={data.description_es || ''}
                name="description_es"
                onChange={onChange}
                />
                <GeneralInput
                label="Description (pt)"
                value={data.description_pt || ''}
                name="description_pt"
                onChange={onChange}
                />
            </div>
            <div className="actions"
            style={{marginTop: '20px'}}
            >
                <button type="submit">Submit</button>
            </div>
            <div className="close" title="close"
            onClick={() => setIsCreateZoneModalOpen(false)}
            >
                <AiOutlineCloseCircle/>
            </div>
        </Modal>
    )
}